import React, { useState, useRef, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { useSelector } from 'react-redux';
import { selectDealerId, selectSiteUseId } from '../../../store/slices/userParamsSlice';

import CustomerDeclarationDialog from './components/dialogs/declaration-form/CustomerDeclarationDialog';
import CustomerDeclarationTable from './components/dialogs/CustomerDeclarationTable';
import CustomerDeclarationFilter from './components/dialogs/CustomerDeclarationFilter';
import GenericFileExportButton from '../../../components/common/GenericFileExportButton';
import useDeclarationForm from './hooks/useDeclarationForm';
import {
    useGetDeclarationsQuery,
    useAddDeclarationMutation,
    useUpdateDeclarationMutation,
    useDeleteDeclarationMutation,
    useLazyExportToExcelQuery
} from '../../../api/customerDeclarationApi';
import { useLanguage } from '../../../contexts/LanguageContext';

const CustomerDeclarationPage = () => {
    const toast = useRef(null);
    const { t, getCurrentLanguageId } = useLanguage();

    const siteUseId = useSelector(selectSiteUseId);
    const dealerId = useSelector(selectDealerId);

    const [addDeclaration] = useAddDeclarationMutation();
    const [updateDeclaration] = useUpdateDeclarationMutation();
    const [deleteDeclaration] = useDeleteDeclarationMutation();

    const [pagination, setPagination] = useState({ PageNumber: 1, PageSize: 10 });

    const {
        formData,
        setFormData,
        errors,
        dialogVisible,
        editMode,
        filters,
        chassisData,
        setChassisData,
        handleNew,
        handleEdit,
        handleSave: getFormDataForSave,
        handleDelete: getDeleteId,
        handleClose,
        handleApplyFilters,
        handleResetFilters,
        updatedFiles,
        onFilesChange,
        onRemoveFile
    } = useDeclarationForm();

    const queryParams = useMemo(() => {
        let dealerIds = [];
        if (filters.dealerId) {
            if (filters.dealerId.isAllDealers) {
                dealerIds = [];
            } else if (Array.isArray(filters.dealerId.dealerIds)) {
                dealerIds = filters.dealerId.dealerIds;
            }
        }

        let chassisNumbers = [];
        if (filters.chassisNo) {
            chassisNumbers = Array.isArray(filters.chassisNo)
                ? filters.chassisNo
                : [filters.chassisNo];
        }

        let countries = [];
        if (filters.country) {
            countries = Array.isArray(filters.country)
                ? filters.country
                : [filters.country];
        }

        let licensePlates = [];
        if (filters.licensePlate) {
            licensePlates = typeof filters.licensePlate === 'string'
                ? [filters.licensePlate]
                : (Array.isArray(filters.licensePlate) ? filters.licensePlate : []);
        }

        const formatDate = (date) => {
            if (!date) return null;
            if (date instanceof Date) {
                return date.toISOString();
            }
            return date;
        };

        return {
            PageNumber: pagination.PageNumber,
            PageSize: pagination.PageSize,
            LanguageId : getCurrentLanguageId(),
            Id: filters.id ? Number(filters.id) : null,
            ChassisNo: chassisNumbers.length > 0 ? chassisNumbers : null,
            LicensePlate: licensePlates.length > 0 ? licensePlates : null,
            DealerId: dealerIds.length > 0 ? dealerIds : null,
            Country: countries.length > 0 ? countries : null,
            CustomerId: filters.customerId || null,
            Status: filters.status || null,
            StartDate: formatDate(filters.startDate),
            EndDate: formatDate(filters.endDate),
            IsInvoiced: filters.isInvoiced != null ? filters.isInvoiced : null
        };
    }, [pagination, filters]);

    const exportParams = useMemo(() => {
        let dealerIds = [];
        if (filters.dealerId) {
            if (filters.dealerId.isAllDealers) {
                dealerIds = [];
            } else if (Array.isArray(filters.dealerId.dealerIds)) {
                dealerIds = filters.dealerId.dealerIds;
            }
        }

        let chassisNumbers = [];
        if (filters.chassisNo) {
            chassisNumbers = Array.isArray(filters.chassisNo)
                ? filters.chassisNo
                : [filters.chassisNo];
        }

        let licensePlates = [];
        if (filters.licensePlate) {
            licensePlates = typeof filters.licensePlate === 'string'
                ? [filters.licensePlate]
                : (Array.isArray(filters.licensePlate) ? filters.licensePlate : []);
        }

        const formatDate = (date) => {
            if (!date) return null;
            if (date instanceof Date) {
                return date.toISOString();
            }
            return date;
        };

        return {
            Id: filters.id ? Number(filters.id) : null,
            ChassisNo: chassisNumbers.length > 0 ? chassisNumbers[0] : null,
            LicensePlate: licensePlates.length > 0 ? licensePlates[0] : null,
            DealerId: dealerIds.length > 0 ? dealerIds[0] : null,
            CustomerId: filters.customerId || null,
            Status: filters.status || null,
            StartDate: formatDate(filters.startDate),
            EndDate: formatDate(filters.endDate),
            IsInvoiced: filters.isInvoiced != null ? filters.isInvoiced : null
        };
    }, [filters]);

    const { data: declarationsResponse = {}, isLoading, isFetching } = useGetDeclarationsQuery(queryParams);

    const handlePageChange = (event) => {
        setPagination({ PageNumber: event.page + 1, PageSize: event.rows });
    };

    const handleFilterApply = (appliedFilters) => {
        handleApplyFilters(appliedFilters);
        setPagination(prev => ({ ...prev, PageNumber: 1 }));
    };

    const handleFilterReset = () => {
        handleResetFilters();
        setPagination({ PageNumber: 1, PageSize: 10 });
    };

    const handleSaveOperation = async () => {
        try {
            const dataToSave = getFormDataForSave();
            if (!dataToSave) return null;

            let resultId = null;

            if (editMode) {
                await updateDeclaration({ id: formData.id, formData: dataToSave }).unwrap();
                toast.current.show({ severity: 'success', summary: t('common.success'), detail: t('warranty.customerDeclaration.messages.updateSuccess') });
                resultId = formData.id;
            } else {
                const createPayload = { ...dataToSave, dealerId: dealerId || 0, siteUseId: siteUseId || 0 };
                const createdId = await addDeclaration(createPayload).unwrap();
                toast.current.show({ severity: 'success', summary: t('common.success'), detail: t('warranty.customerDeclaration.messages.createSuccess') });
                resultId = createdId;
            }

            return resultId ? { id: resultId } : null;

        } catch (error) {
            const msg = error?.data?.errorMessage || error?.message || t('warranty.customerDeclaration.messages.error');
            toast.current.show({ severity: 'error', summary: t('common.error'), detail: msg });
            return null;
        }
    };

    const handleDeleteOperation = () => {
        confirmDialog({
            message: t('warranty.customerDeclaration.messages.deleteConfirm'),
            header: t('warranty.customerDeclaration.messages.deleteHeader'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            acceptLabel: t('common.yes'),
            rejectLabel: t('common.no'),
            accept: async () => {
                try {
                    const id = getDeleteId();
                    if (id) {
                        await deleteDeclaration(id).unwrap();
                        toast.current.show({ severity: 'success', summary: t('common.success'), detail: t('warranty.customerDeclaration.messages.deleteSuccess') });
                        handleClose();
                    }
                } catch (error) {
                    console.error(error);
                }
            }
        });
    };

    return (
        <div className="card">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0 text-3xl text-900">{t('warranty.customerDeclaration.title')}</h1>
                <GenericFileExportButton
                    useExportQuery={useLazyExportToExcelQuery}
                    exportParams={exportParams}
                    buttonLabel={t('common.export')}
                    fileNameExtractor={() => 'MusteriBeyanlari.xlsx'}
                    dataExtractor={(result) => result?.fileBase64}
                />
            </div>

            <CustomerDeclarationFilter
                handleApplyFilters={handleFilterApply}
                handleResetFilters={handleFilterReset}
            />

            <CustomerDeclarationTable
                declarations={declarationsResponse.items || []}
                totalCount={declarationsResponse.totalCount || 0}
                loading={isLoading || isFetching}
                rows={pagination.PageSize}
                first={(pagination.PageNumber - 1) * pagination.PageSize}
                onPageChange={handlePageChange}
                handleNew={handleNew}
                handleEdit={handleEdit}
            />

            <CustomerDeclarationDialog
                visible={dialogVisible}
                onHide={handleClose}
                formData={formData}
                setFormData={setFormData}
                errors={errors}
                handleSave={handleSaveOperation}
                editMode={editMode}
                handleDelete={handleDeleteOperation}
                chassisData={chassisData}
                setChassisData={setChassisData}
                updatedFiles={updatedFiles}
                onFilesChange={onFilesChange}
                onRemoveFile={onRemoveFile}
            />
        </div>
    );
};

export default CustomerDeclarationPage;
